import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, ZapIcon, DatabaseIcon, LockIcon, ActivityIcon, BuildingIcon, ShoppingCartIcon, CodeIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WebsiteHostingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website hosting | Snelle & veilige hosting | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Betrouwbare website hosting voor WordPress. Snelle servers, SSL-certificaat, dagelijkse backups en 99.9% uptime. Hosting speciaal geoptimaliseerd voor jouw website.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website hosting | Snelle & veilige hosting | Noord',
      'og:description': 'Betrouwbare website hosting voor WordPress. Snelle servers, SSL-certificaat, dagelijkse backups en 99.9% uptime.',
      'og:type': 'website',
      'og:url': 'https://noorddigital.nl/website-hosting',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Is hosting verplicht voor een website?',
    a: 'Ja, elke website heeft hosting nodig om online te zijn. Of je nu een kleine bedrijfswebsite hebt of een uitgebreide webshop, zonder hosting is je website niet bereikbaar voor bezoekers. Wij regelen betrouwbare hosting zodat jij je geen zorgen hoeft te maken over techniek.'
  }, {
    q: 'Zit hosting inbegrepen bij een nieuwe website?',
    a: 'Ja, bij Noord Digital is hosting standaard inbegrepen wanneer je een website bij ons laat maken. Je krijgt snelle servers, dagelijkse backups, SSL-beveiliging en monitoring. Alles in één pakket zonder verborgen kosten.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-1954" data-mp="23yti1n8pbs95">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-1955" data-mp="1z461cajxw254">
        <div className="absolute inset-0 pointer-events-none" data-id="element-1956" data-mp="1u5mgyekzuyev">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-1957" data-mp="2rlrum9tpag1s" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-1958" data-mp="2mv33wwpuhn3p" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-1959" data-mp="2v5ah4afswirl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1960" data-mp="394rza6ylcxrz">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-1961" data-mp="3e21uk38nw3nq">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-1962" data-mp="3i2vlz6lbc0ld">
              Home
            </a>
            <span className="text-navy-500" data-id="element-1963" data-mp="3rurjaffkrmoj">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-1964" data-mp="3rtgfifm4qexf">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-1965" data-mp="5ntd9rblazav">/</span>
            <span className="text-accent-light font-medium" data-id="element-1966" data-mp="12e57kp801fam">
              Website hosting
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-1967" data-mp="xg3oi4akpfz5">
            Snelle en veilige website hosting
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-1968" data-mp="17556u14lc9ga">
            Betrouwbare hosting voor professionele websites. Razendsnel, altijd
            beveiligd en volledig beheerd door ons.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-1969" data-mp="ei0vzfith0wl">
            <a href="#hosting-voordelen" className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-1970" data-mp="sgqrwrtwgujd">
              Bekijk hosting
              <ArrowRightIcon className="w-5 h-5" data-id="element-1971" data-mp="1zuc30tgkomyd" />
            </a>
          </div>
        </div>
      </section>

      {/* Hosting voordelen */}
      <section id="hosting-voordelen" className="py-24 bg-surface-muted" data-id="element-1972" data-mp="27bqrv9hpyvjv">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1973" data-mp="2cdfzsg9nat3q">
          <div className="text-center mb-16" data-id="element-1974" data-mp="1xfdvg3usi3lf">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1975" data-mp="1xgozm4s5ddd2">
              Waarom kiezen voor onze website hosting?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1976" data-mp="2q431mhapcz41">
              Onze hosting is geoptimaliseerd voor snelheid, veiligheid en
              betrouwbaarheid.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1977" data-mp="2gejh777ccx72">
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-1978" data-mp="2ld353d5hwj22">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1979" data-mp="2lb9x9uj5bssh">
                <ZapIcon className="w-6 h-6 text-accent" data-id="element-1980" data-mp="390knwj6s86sw" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1981" data-mp="3duynyywe323w">
                Snelle servers
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1982" data-mp="3nn9yph6hi6el">
                Onze servers zijn geoptimaliseerd voor WordPress. Jouw website
                laadt razendsnel, wat zorgt voor een betere gebruikerservaring
                en hogere Google-ranking.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-1983" data-mp="39pbnulrdgs3u">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1984" data-mp="34rjsfhriqnuw">
                <DatabaseIcon className="w-6 h-6 text-accent" data-id="element-1985" data-mp="2zz07u0ueysgl" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1986" data-mp="2v1qjypmoaouv">
                Dagelijkse backups
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1987" data-mp="2r0oi011dfyp7">
                Elke dag wordt er automatisch een volledige backup gemaakt van
                jouw website en database. Bij problemen herstellen we alles
                snel.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-1988" data-mp="wjof0a4f6e4m">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1989" data-mp="11hzoukupsomu">
                <LockIcon className="w-6 h-6 text-accent" data-id="element-1990" data-mp="16hce1c9267sl" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1991" data-mp="16f8ch0ymtkvh">
                SSL-beveiliging
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1992" data-mp="dsnkbyac14qv">
                Gratis SSL-certificaat inbegrepen. Jouw website is beveiligd met
                HTTPS, wat vertrouwen wekt bij bezoekers en door Google wordt
                beloond.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-1993" data-mp="mr8x1ct4hpjq">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1994" data-mp="rld55ze4k3me">
                <ActivityIcon className="w-6 h-6 text-accent" data-id="element-1995" data-mp="3r71rply0znjp" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1996" data-mp="3v7c6no3spoap">
                24/7 Monitoring
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1997" data-mp="2onnk3lnnphib">
                Wij monitoren jouw website continu op uptime en prestaties. Bij
                problemen grijpen we direct in, vaak voordat jij het merkt.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Voor wie */}
      <section className="py-24 bg-white" data-id="element-1998" data-mp="37kfbyjh9l4xz">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1999" data-mp="37jdotgmz4rzw">
          <div className="text-center mb-16" data-id="element-2000" data-mp="32llw4kei91un">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2001" data-mp="32ksyqa1frop6">
              Voor wie is onze hosting geschikt?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2002" data-mp="3cb5e7nue8rog">
              Onze snelle website hosting is geschikt voor uiteenlopende
              websites en bedrijven.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2003" data-mp="3m9l5prxc22yh">
            <div className="border border-navy-100 rounded-card p-8 text-center" data-id="element-2004" data-mp="3m78jh2kjl6fm">
              <div className="w-14 h-14 rounded-full bg-accent-light flex items-center justify-center mx-auto mb-6" data-id="element-2005" data-mp="8t0wnn05ypxh">
                <BuildingIcon className="w-7 h-7 text-accent" data-id="element-2006" data-mp="dp81fcgtpvdu" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2007" data-mp="3vwntjoren7y6">
                Bedrijven
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2008" data-mp="4sqqoacdfzim">
                Betrouwbare hosting voor bedrijfswebsites die altijd online en
                snel bereikbaar moeten zijn voor klanten.
              </p>
            </div>
            <div className="border border-navy-100 rounded-card p-8 text-center" data-id="element-2009" data-mp="3d6ze7401e03x">
              <div className="w-14 h-14 rounded-full bg-accent-light flex items-center justify-center mx-auto mb-6" data-id="element-2010" data-mp="3h8142u82czt8">
                <ShoppingCartIcon className="w-7 h-7 text-accent" data-id="element-2011" data-mp="337qcfoyxjh70" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2012" data-mp="38739t3nfg6nu">Webshops</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2013" data-mp="3897brg1rmvlh">
                Snelle en veilige hosting voor webshops waar betrouwbaarheid en
                laadsnelheid direct invloed hebben op de omzet.
              </p>
            </div>
            <div className="border border-navy-100 rounded-card p-8 text-center" data-id="element-2014" data-mp="2bomlq6aml97r">
              <div className="w-14 h-14 rounded-full bg-accent-light flex items-center justify-center mx-auto mb-6" data-id="element-2015" data-mp="2gmo5ktfvlcka">
                <CodeIcon className="w-7 h-7 text-accent" data-id="element-2016" data-mp="34c8mz7wds1rg" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2017" data-mp="39bbssuk6qc0h">
                WordPress websites
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2018" data-mp="2ulnr3s83dbc8">
                Hosting die speciaal is geoptimaliseerd voor WordPress, met de
                juiste serverinstellingen voor maximale prestaties.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-surface-muted border-y border-navy-100" data-id="element-2019" data-mp="3s3nrn7bmqv5v">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2020" data-mp="1gm2gfp1gvend">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-2021" data-mp="1boj2yrclf5wh">
            Gerelateerde pagina's:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2022" data-mp="1pecr17g9xm55">
            <a href="#/hosting-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2023" data-mp="1ldssbzg1yagw">
              Website onderhoud
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2024" data-mp="spoq5nwowfiy">•</span>
            <a href="#/hosting-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2025" data-mp="srhsir09sdl5">
              Website beveiliging
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2026" data-mp="2i3k030dctu4n">•</span>
            <a href="#/wordpress-websites" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2027" data-mp="2i5wfb5rq9qe0">
              WordPress websites
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-id="element-2028" data-mp="1foubhq1t19q0">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2029" data-mp="1jrqxlqqjznyp">
          <div className="text-center mb-16" data-id="element-2030" data-mp="1jqft1opaac6j">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2031" data-mp="1orlk7i3aprdy">
              Veelgestelde vragen over website hosting
            </h2>
          </div>

          <div className="space-y-4" data-id="element-2032" data-mp="w2o7duhusmsl">
            {faqs.map((faq, index) => <div key={index} className="bg-surface-muted rounded-xl shadow-sm border border-navy-50 overflow-hidden" data-id="element-2033" data-mp="w0le91lemdjn" data-mpi={index}>
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-2034" data-mp="15t9cm0s18pkh">
                  <span className="font-bold text-navy-900" data-id="element-2035" data-mp="dalvsxk2jwp7">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-2036" data-mp="5ql2iqrbvopp" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-2037" data-mp="rhz1aqzxqju" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-2038" data-mp="3nxqzl120c3xh">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-2039" data-mp="399e382z0cj2g" />
    </main>;
}